import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, FileText, UserRound } from 'lucide-react';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { newIdempotencyKey } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Button, DatePicker, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { PatientPicker } from '../patients/PatientPicker';

interface DraftLine {
  key: string;
  chargeItemId: string;
  quantity: string;
  approvalNumber: string;
}

export function ManualPreAuthPage({ returnTo }: { returnTo: string }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const nphiesWording = useAuth().hasModule('nphies');
  const [patientId, setPatientId] = useState('');
  const [payerId, setPayerId] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [notes, setNotes] = useState('');
  const [authFrom, setAuthFrom] = useState('');
  const [authTo, setAuthTo] = useState('');
  const [search, setSearch] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [error, setError] = useState<string | null>(null);

  const payers = useQuery({ queryKey: ['payers', 'branch'], queryFn: () => get<{ id: string; name: string }[]>('/insurance/payers?availableInBranch=true') });
  const charges = useQuery({
    queryKey: ['charge-items', 'for-preauth', search],
    queryFn: () => get<{ data: { id: string; code: string; name: string }[] }>(`/charge-items${qs({ q: search || undefined, limit: 30 })}`),
  });
  const chargeName = (id: string) => charges.data?.data.find((row) => row.id === id)?.name ?? id;

  const save = useMutation({
    mutationFn: () =>
      post('/insurance/pre-auths', {
        patientId,
        payerId,
        diagnosis: diagnosis.trim() || undefined,
        notes: notes.trim() || undefined,
        authFrom: authFrom || undefined,
        authTo: authTo || undefined,
        lines: lines.map((line) => ({ chargeItemId: line.chargeItemId, quantity: Number(line.quantity) || 1, ...(line.approvalNumber.trim() ? { approvalNumber: line.approvalNumber.trim() } : {}) })),
      }),
    onSuccess: () => {
      toast.success('Pre-authorisation recorded');
      void queryClient.invalidateQueries({ queryKey: ['pre-auth-tab'] });
      void queryClient.invalidateQueries({ queryKey: ['pre-auth-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['pre-auth-detail'] });
      void queryClient.invalidateQueries({ queryKey: ['pre-auth-needed'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = Boolean(patientId && payerId) && (lines.length > 0 || diagnosis.trim().length > 0);
  const dirty = !save.isSuccess && Boolean(patientId || payerId || diagnosis || notes || authFrom || authTo || lines.length > 0);

  return (
    <FormScreen
      title={nphiesWording ? 'Record a pre-authorisation given outside NPHIES' : 'Record a pre-authorisation'}
      description="For approvals the payer gives by letter, phone or portal. Each service can carry its own approval number."
      icon={ClipboardList}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) save.mutate();
      }}
    >
      <FormSection title={tr('billing', 'patient')} icon={UserRound}>
        <Field label="Patient" required>
          <PatientPicker value={patientId} onChange={setPatientId} />
        </Field>
        <Field label="Payer" required>
          <Select value={payerId} onChange={(e) => setPayerId(e.target.value)}>
            <option value="">Choose…</option>
            {payers.data?.map((payer) => (
              <option key={payer.id} value={payer.id}>
                {payer.name}
              </option>
            ))}
          </Select>
        </Field>
      </FormSection>

      <FormSection title={tr('billing', 'request_details')} icon={FileText}>
        <Field label="Diagnosis" className="sm:col-span-2">
          <Input value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} />
        </Field>
        <Field label="Approved from">
          <DatePicker value={authFrom} onChange={(value) => setAuthFrom(value)} />
        </Field>
        <Field label="Approved until">
          <DatePicker value={authTo} onChange={(value) => setAuthTo(value)} />
        </Field>
        <Field label="Notes" className="sm:col-span-2">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </FormSection>

      <FormSection title={tr('billing', 'services')} icon={ClipboardList} columns={1}>
        <Field label="Add a service">
          <div className="flex gap-2">
            <Input placeholder="Search the charge master" value={search} onChange={(e) => setSearch(e.target.value)} />
            <Select
              value=""
              onChange={(e) => {
                if (e.target.value) setLines([...lines, { key: newIdempotencyKey(), chargeItemId: e.target.value, quantity: '1', approvalNumber: '' }]);
              }}
            >
              <option value="">Choose…</option>
              {charges.data?.data.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.code} — {row.name}
                </option>
              ))}
            </Select>
          </div>
        </Field>
        {lines.map((line, index) => (
          <div key={line.key} className="grid gap-2 rounded-md border p-2 sm:grid-cols-4">
            <span className="self-center text-sm sm:col-span-2">{chargeName(line.chargeItemId)}</span>
            <Input type="number" min={1} value={line.quantity} onChange={(e) => setLines(lines.map((row, at) => (at === index ? { ...row, quantity: e.target.value } : row)))} />
            <div className="flex gap-1">
              <Input placeholder="Approval number" value={line.approvalNumber} onChange={(e) => setLines(lines.map((row, at) => (at === index ? { ...row, approvalNumber: e.target.value } : row)))} />
              <Button type="button" size="sm" variant="ghost" onClick={() => setLines(lines.filter((_, at) => at !== index))}>
                Remove
              </Button>
            </div>
          </div>
        ))}
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={tr('billing', 'save')} loading={save.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
